'use no memo';

import {Action, Card} from 'platform/components';
import {Grid, GridItem, Hide, Space} from 'platform/foundation';

import {useCallback, useEffect, useState} from 'react';

import {isNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  NewOfferPurchaseVehicleResponseBody,
  PatchBuyingOfferPurchaseVehicleRequestBody,
} from '@dms/api/finance';
import {
  useGetBusinessCaseQuery,
  useGetVehicleQuery,
  usePatchBuyingOfferPurchaseVehicleMutation,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {CommentsWidget, handleApiError, usePermissions} from '@dms/shared';
import {WithValidationErrors} from '@dms/teas';

import {buildArray, useRequiredParams} from 'shared/web';

import {BusinessCasePurchaseVehicleBuyingDetail} from './components/BusinessCasePurchaseVehicleBuyingDetail';
import {BusinessCasePurchaseVehicleBuyingForm} from './components/BusinessCasePurchaseVehicleBuyingForm';

type BuyingTabProps = {
  purchaseVehicle: NewOfferPurchaseVehicleResponseBody;
};

export const BusinessCasePurchaseVehicleBuying = ({purchaseVehicle}: BuyingTabProps) => {
  const {id: businessCaseId} = useRequiredParams();
  const {data: businessCase} = useGetBusinessCaseQuery({businessCaseId});
  const [patchBuyingVehicle] = usePatchBuyingOfferPurchaseVehicleMutation();

  const editable = businessCase?.actions?.purchaseEditVehicle;
  const offer = businessCase?.offers?.[0];

  const {data: vehicleData} = useGetVehicleQuery(
    {vehicleId: purchaseVehicle?.vehicleId ?? ''},
    {skip: isNil(purchaseVehicle?.vehicleId)}
  );

  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: purchaseVehicle?.vehicleId ?? '',
    },
    {skip: isNil(purchaseVehicle?.vehicleId)}
  );

  const [
    hasVehicleCommentReadPermission,
    hasVehicleCommentAddPermission,
    hasVehicleCommentDeletePermission,
  ] = usePermissions({
    permissionKeys: ['vehicleCommentRead', 'vehicleCommentAdd', 'vehicleCommentDelete'],
    scopes: {
      vehicleCommentAdd: {
        participation: vehicleParticipation,
        branchId: vehicleData?.branchId,
      },
      vehicleCommentRead: {
        participation: vehicleParticipation,
        branchId: vehicleData?.branchId,
      },
      vehicleCommentDelete: {
        participation: vehicleParticipation,
        branchId: vehicleData?.branchId,
      },
    },
  });

  const [isDetail, setDetail] = useState(
    (purchaseVehicle?.buying.confirmed && editable) || !editable
  );

  const [showDiscard, setShowDiscard] = useState(!!purchaseVehicle?.buying?.confirmed);
  const toggleDetail = useCallback(() => setDetail(!isDetail), [isDetail]);

  const title = !isDetail ? undefined : i18n.t('entity.businessCase.labels.buying');

  const handleSubmit = useCallback(
    async (
      values: PatchBuyingOfferPurchaseVehicleRequestBody
    ): Promise<WithValidationErrors<void>> => {
      if (isNil(offer?.id)) {
        return;
      }

      await patchBuyingVehicle({
        offerId: offer.id,
        patchBuyingOfferPurchaseVehicleRequestBody: values,
        businessCaseId,
        offerPurchaseVehicleId: purchaseVehicle?.id as string,
      })
        .unwrap()
        .catch(handleApiError);

      toggleDetail();
      setShowDiscard(true);
    },
    [offer, patchBuyingVehicle, businessCaseId, purchaseVehicle, toggleDetail]
  );

  useEffect(
    function syncDetailView() {
      setDetail((purchaseVehicle?.buying.confirmed && editable) || !editable);
    },
    [purchaseVehicle?.buying.confirmed, editable]
  );

  return (
    <Grid columns={1}>
      <GridItem>
        <Grid columns={[1, 2]}>
          <GridItem>
            <Card
              title={title}
              actions={buildArray<Action>().when(isDetail && editable, {
                type: 'button',
                variant: 'link',
                leftIcon: 'image/edit',
                onClick: toggleDetail,
                title: i18n.t('general.actions.edit'),
                'data-testid': testIds.businessCase.buying('buying-edit'),
              })}
              data-testid={testIds.businessCase.buying('buying')}
              variant="inlineWhite"
            >
              {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
              {purchaseVehicle && (
                <>
                  {isDetail ? (
                    <BusinessCasePurchaseVehicleBuyingDetail values={purchaseVehicle.buying} />
                  ) : (
                    <>
                      <Space vertical={4} />
                      <BusinessCasePurchaseVehicleBuyingForm
                        maxBuyingPrice={purchaseVehicle.pricing?.maxBuyingPrice ?? undefined}
                        onSubmit={handleSubmit}
                        onDiscard={toggleDetail}
                        showDiscard={showDiscard}
                        initialValues={purchaseVehicle.buying}
                      />
                    </>
                  )}
                </>
              )}
            </Card>
          </GridItem>
          <Hide when={isNil(purchaseVehicle?.vehicleId) || !hasVehicleCommentReadPermission}>
            <CommentsWidget
              title={i18n.t('entity.vehicle.labels.vehicleComments')}
              resourceId={EntityResourceIds.vehicle}
              recordId={purchaseVehicle.vehicleId}
              isAddDisabled={!hasVehicleCommentAddPermission}
              isDeleteDisabled={!hasVehicleCommentDeletePermission}
            />
          </Hide>
        </Grid>
      </GridItem>
    </Grid>
  );
};
